// ──────────────────────────────────────────────────────
// BudgetCard — compact value/alternative card (role='budget').
//
// QCARE-AI-DEV-FRONT-DARK-CARDS-V2 (2026-05-09): rebuilt to match the
// reference dark-premium design.
//
// Layout: same CSS Grid as HeroCard (`minmax(96px, 28%) 1fr`) so the
// product image sits on the LEFT (visually) with a stable, smaller fixed
// width and the text content fills the right column. Grid container
// uses `direction: 'ltr'`; only the inner text panel re-applies `rtl`.
//
// Shell border: subtle teal regardless of label kind. The PRIOR version
// painted a strong amber border around the whole card when labelKind was
// 'value' — that read as "warning yellow". Per the v2 spec, gold/amber
// is now scoped to the small "أفضل قيمة 💰" badge ONLY.
//
// Accent branching (R6J — preserved):
//   • labelKind='alternative' (R6J fallback) → teal badge "بديل مناسب 🔄"
//   • otherwise → amber badge "أفضل قيمة 💰"
//   • savings pill (green "وفر N٪") only if savingsPercent > 0
//
// Trust: single consolidated `<TrustBar size="sm">` at the bottom.
// ──────────────────────────────────────────────────────
import Link from 'next/link';
import type { ProductSuggestion } from '@/lib/types';
import { TrustBar } from './TrustBar';
import { isProductOutOfStock, OUT_OF_STOCK_LABEL_AR } from './availability-badge';

const CYAN_PRIMARY = '#35E7F2';
const CYAN_SOFT = '#a8dce4';
const TEXT_PRIMARY = '#ffffff';
const TEXT_MUTED = 'rgba(255,255,255,0.55)';
const SHELL_BORDER = 'rgba(80, 200, 200, 0.32)';
// QCARE-AI-DEV-FRONT-SECONDARY-NO-REASONS (2026-05-09): TEXT_BODY +
// DIVIDER tokens were used by the now-removed reasons block (divider
// line + bullet text). Re-introduce them locally if reasons are ever
// re-enabled on the secondary card.

export function BudgetCard({ product }: { product: ProductSuggestion }) {
    const ps = product;
    // QCARE-AI-DEV-FRONT-SECONDARY-NO-REASONS (2026-05-09): the reasons
    // bullet block ("ليش هو أوفر" / "ليش يستاهل التجربة" + 2 bullets) is
    // suppressed on the secondary card per the v2 design. The data
    // (`ps.reasons` / `ps.shortReasonAr` / `ps.reasonsHeader`) is still
    // received from the backend and would re-render trivially if the
    // block below is reinstated.
    const hasSavings = typeof ps.savingsPercent === 'number' && ps.savingsPercent > 0;
    const outOfStock = isProductOutOfStock(ps);

    // R6J: backend sets labelKind='alternative' when isFallbackBudget=true
    // (alt is NOT meaningfully cheaper than the hero) so the budget label
    // becomes "بديل مناسب 🔄" instead of "أفضل قيمة 💰". The accent on the
    // small label badge follows: teal for alternative, amber for true budget.
    const isAlternative = ps.labelKind === 'alternative' || ps.icon === '🔄';

    // QCARE-AI-DEV-FRONT-BADGE-GLASS (2026-05-09): glass-style badge tokens.
    // Replaces the solid cyan/amber gradient with translucent layers + a
    // semi-opaque border + soft outer glow + inset highlight. `text` is
    // kept high-contrast on the dark card. `accent` + `accentBg` are kept
    // separate so the reasons-list bullet dot + reasonsHeader retain a
    // brighter accent color (the bullet dot would lose visibility against
    // the dark shell if we used the new translucent text color directly).
    const badge = isAlternative
        ? {
            bg: 'linear-gradient(135deg, rgba(80, 200, 200, 0.22), rgba(0, 255, 255, 0.10))',
            border: 'rgba(80, 200, 200, 0.55)',
            text: '#BDFBFF',
            glow: 'rgba(80, 200, 200, 0.18)',
            accent: CYAN_PRIMARY,
            accentBg: 'rgba(80, 200, 200, 0.20)',
        }
        : {
            bg: 'linear-gradient(135deg, rgba(255, 193, 77, 0.22), rgba(255, 193, 77, 0.08))',
            border: 'rgba(255, 193, 77, 0.55)',
            text: '#FFD36A',
            glow: 'rgba(255, 193, 77, 0.14)',
            accent: '#fbbf24',
            accentBg: 'rgba(255, 193, 77, 0.18)',
        };

    return (
        <Link
            href={ps.href}
            style={{
                display: 'block',
                borderRadius: 16,
                overflow: 'hidden',
                cursor: 'pointer',
                textDecoration: 'none',
                background:
                    'radial-gradient(circle at 88% 10%, rgba(80, 200, 200, 0.14), transparent 36%), '
                    + 'linear-gradient(145deg, #132633 0%, #0E1A24 45%, #071016 100%)',
                border: `1px solid ${SHELL_BORDER}`,
                boxShadow: '0 6px 18px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(96px, 28%) 1fr',
                    alignItems: 'stretch',
                    direction: 'ltr',
                    minHeight: 130,
                }}
            >
                {/* Image panel — LEFT, stable width, soft cream surface.
                    QCARE-AI-DEV-FRONT-SECONDARY-NO-REASONS (2026-05-09):
                    `alignItems` switched from `'center'` to `'flex-start'`
                    so the image panel sits at the TOP of the grid cell —
                    its top edge now visually aligns with the secondary
                    badge ("أفضل قيمة 💰" / "بديل مناسب 🔄") instead of
                    being vertically centered against the now-shorter text
                    column. Width / height of the inner panel are
                    UNCHANGED (still `clamp(110px, 18vw, 150px)` height
                    bounded by the unchanged `minmax(96px, 28%)` column). */}
                <div
                    style={{
                        padding: 10,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                    }}
                >
                    <div
                        // QCARE-AI-DEV-FRONT-BUDGET-IMG-HEIGHT (2026-05-09):
                        // budget/alternative image panel was previously bound
                        // by `aspectRatio: '1 / 1'` — making the panel a
                        // square sized by the column width. The image-cell
                        // wrapper centers the square against the full row
                        // height, but on wider chat bubbles the square
                        // (~130-180 px tall) was almost reaching the trust
                        // bar and visually dominating the card.
                        //
                        // Replaced with an explicit clamp height that's
                        // substantially shorter than the natural square so
                        // the panel bottom lands around the divider before
                        // the reasons section ("ليش هو أوفر") instead of
                        // extending all the way down to the trust pill.
                        // Width stays at `100%` of the column (which is
                        // unchanged at `minmax(96px, 28%)`) so the visual
                        // width isn't touched.
                        //
                        // Hero card is NOT affected — HeroCard.tsx has its
                        // own separate image panel with `clamp(160px, 30vw,
                        // 240px)` height and stays exactly as-is.
                        style={{
                            background: '#F3F7F8',
                            borderRadius: 18,
                            padding: 12,
                            width: '100%',
                            height: 'clamp(110px, 18vw, 150px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'inset 0 0 0 1px rgba(80,200,200,0.10)',
                        }}
                    >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={ps.imageUrl}
                            alt={ps.nameAr}
                            className="q-card-image-fade"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                            onLoad={(e) => {
                                const el = e.target as HTMLImageElement;
                                if (el.dataset.fallback === '1') return;
                                el.style.opacity = '1';
                            }}
                            onError={(e) => {
                                const el = e.target as HTMLImageElement;
                                if (el.dataset.fallback === '1') return;
                                el.dataset.fallback = '1';
                                el.src = '/branding/qio-logo.jpeg';
                                el.style.opacity = '0.55';
                            }}
                        />
                    </div>
                </div>

                {/* Text panel — RTL flow, badge top, title/brand/price middle,
                    bullets, single consolidated TrustBar at the bottom. */}
                <div
                    style={{
                        direction: 'rtl',
                        padding: '12px 14px 12px 6px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        minWidth: 0,
                        gap: 8,
                    }}
                >
                    {/* Header row — small label badge + optional OOS + savings pills. */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
                        {ps.label ? (
                            <span
                                // QCARE-AI-DEV-FRONT-BADGE-GLASS (2026-05-09):
                                // glass-style badge. Geometry (padding /
                                // radius / fontSize / icon-text gap)
                                // unchanged.
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    padding: '3px 8px',
                                    borderRadius: 999,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: '0.02em',
                                    color: badge.text,
                                    background: badge.bg,
                                    border: `1px solid ${badge.border}`,
                                    boxShadow: `0 0 16px ${badge.glow}, inset 0 1px 0 rgba(255, 255, 255, 0.08)`,
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                }}
                            >
                                {ps.icon && <span aria-hidden>{ps.icon}</span>}
                                <span>{ps.label}</span>
                            </span>
                        ) : (
                            <span />
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {outOfStock && (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        padding: '2px 6px',
                                        borderRadius: 6,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        background: 'rgba(180,35,24,0.15)',
                                        color: '#fca5a5',
                                        border: '1px solid rgba(252,165,165,0.40)',
                                    }}
                                    aria-label={OUT_OF_STOCK_LABEL_AR}
                                >
                                    {OUT_OF_STOCK_LABEL_AR}
                                </span>
                            )}
                            {hasSavings && (
                                <span
                                    style={{
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        background: '#10b981',
                                        color: '#ffffff',
                                    }}
                                >
                                    وفر {ps.savingsPercent}٪
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Title + brand + price */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <div
                            style={{
                                color: TEXT_PRIMARY,
                                fontWeight: 600,
                                fontSize: 14,
                                lineHeight: 1.35,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {ps.nameAr}
                        </div>
                        {ps.brandAr && (
                            <div style={{ color: TEXT_MUTED, fontSize: 11, fontWeight: 500 }}>
                                {ps.brandAr}
                            </div>
                        )}
                        {ps.price != null && ps.price > 0 && (
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 1 }}>
                                <span
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 700,
                                        color: CYAN_PRIMARY,
                                        letterSpacing: '0.01em',
                                        textShadow: '0 1px 6px rgba(53,231,242,0.25)',
                                        lineHeight: 1,
                                    }}
                                >
                                    {ps.price.toFixed(2)}
                                </span>
                                <span style={{ fontSize: 11, color: CYAN_SOFT, fontWeight: 500 }}>ريال</span>
                            </div>
                        )}
                    </div>

                    {/* QCARE-AI-DEV-FRONT-SECONDARY-NO-REASONS (2026-05-09):
                        reasons block (divider + reasonsHeader "ليش هو أوفر"
                        / "ليش يستاهل التجربة" + 2 bullets) intentionally
                        suppressed on the secondary card per v2 design.
                        Hero card keeps its "ليش الأفضل:" + bullets.
                        `badge.accent` and `badge.accentBg` properties on
                        the badge object are now dormant on this card
                        surface (still defined for parity with hero) — they
                        would automatically re-color the bullet dots if
                        this block is reinstated. */}
                </div>

                {/* QCARE-AI-DEV-FRONT-TRUSTBAR-FULLWIDTH (2026-05-09):
                    trust bar moved OUT of the text column and INTO a
                    full-width grid footer row. `gridColumn: '1 / -1'` spans
                    both image (col 1) + text (col 2) so the green emerald
                    bar stretches the full inner card width — no more empty
                    gap under the image column. Inner padding tighter than
                    HeroCard so the budget card stays compact (10 px sides,
                    10 px bottom). `direction: 'rtl'` re-applied for RTL
                    alignment. */}
                <div
                    style={{
                        gridColumn: '1 / -1',
                        padding: '0 10px 10px 10px',
                        direction: 'rtl',
                    }}
                >
                    <TrustBar size="sm" />
                </div>
            </div>
        </Link>
    );
}
